import { action } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import './profile.css';
import CheckRadio from '../../common/forms/check-radio';
import EducationEditor from '../../common/widgets/EducationEditor';
import InputField from '../../common/forms/input-field';
import JobHistoryEditor from '../../common/widgets/JobHistoryEditor';
import MenteeProgramEditor from '../../common/widgets/MenteeSelfEvaluationEditor';
import PageFrameGeneric from '../../common/page-frames/generic';
import ProfileMenu from './ProfileMenu';
import ProfileSection from './ProfileSection';
import ProfileStore from '../../stores/ProfileStore';
import request from '../../common/utils/Request';
import ScrollTopButton from './ScrollTopButton';

import { DataLoadStatus, Gender, ProfileProps } from '../../types';

@inject('profileStore')
@observer
class Profile extends React.Component<ProfileProps> {
  private store: ProfileStore;

  public constructor(props: ProfileProps) {
    super(props);
    this.store = props.profileStore!;
  }

  public async componentDidMount() {
    await this.props.profileStore.loadProfileData();
  }

  public render() {
    if (this.store.loadStatus === DataLoadStatus.LOADING) {
      return (<div/>);
    }
    
    return (
      <PageFrameGeneric className="profile" title="Profile">
        <div className="columns is-centered is-multiline">
          <div className="column is-3 is-2-widescreen side-bar">
            <ProfileMenu history={this.props.history} />
          </div>

          <div className="column is-7-tablet is-6-desktop is-5-widescreen main-content">
            <ProfileSection title="Personal Information" anchor="basic-info">
              <div className="columns is-multiline">
                <InputField
                    helpMessage={this.store.firstNameHelpMessage}
                    highlight={this.store.firstNameHighlight}
                    label="First Name"
                    layoutClassName="column is-6 is-marginless"
                    onBlur={this.handleFirstNameBlur}
                    onChange={this.handleFirstNameChange}
                    name="firstName"
                    type="text"
                    value={this.store.firstName}
                />

                <InputField
                    helpMessage={this.store.lastNameHelpMessage}
                    highlight={this.store.lastNameHighlight}
                    label="Last Name"
                    layoutClassName="column is-6 is-marginless"
                    onBlur={this.handleLastNameBlur}
                    onChange={this.handleLastNameChange}
                    name="lastName"
                    type="text"
                    value={this.store.lastName}
                />

                <InputField
                    disabled={true}
                    helpMessage="Please contact info@mentorintech.com to change your e-mail address."
                    label="Email"
                    layoutClassName="column is-12 is-marginless"
                    name="email"
                    type="email"
                    value={this.store.email}
                />

                <CheckRadio
                    id="gender"
                    label="Gender"
                    layoutClassName="column is-12 is-marginless"
                    name="gender"
                    onChange={this.handleGenderChange}
                    options={Object.values(Gender)}
                    type="radio"
                    value={this.store.gender}
                />

                <InputField
                    helpMessage={this.store.cityHelpMessage}
                    highlight={this.store.cityHighlight}
                    label="City"
                    layoutClassName="column is-8 is-marginless"
                    name="city"
                    onBlur={this.handleCityBlur}
                    onChange={this.handleCityChange}
                    placeholder="e.g. San Francisco"
                    type="text"
                    value={this.store.city}
                />

                <InputField
                    helpMessage={this.store.stateHelpMessage}
                    highlight={this.store.stateHighlight}
                    label="State"
                    layoutClassName="column is-4 is-marginless"
                    name="state"
                    onBlur={this.handleStateBlur}
                    onChange={this.handleStateChange}
                    placeholder="e.g. California"
                    type="text"
                    value={this.store.state}
                />

                <InputField
                    label="Interests"
                    layoutClassName="column is-12 is-marginless"
                    name="interests"
                    onBlur={this.handleInterestsBlur}
                    onChange={this.handleInterestsChange}
                    placeholder="Press &crarr; to add tags"
                    type="tags"
                    value={this.store.interests}
                />
              </div>
            </ProfileSection>
            
            <ProfileSection title="Job Histories" anchor="job-histories">
              <JobHistoryEditor />
            </ProfileSection>

            <ProfileSection title="Educations" anchor="educations">
              <EducationEditor />
            </ProfileSection>

            <ProfileSection title="Self Evaluation" anchor="self-evaluation">
              <MenteeProgramEditor/>
            </ProfileSection>

          </div>
        </div>

        <ScrollTopButton
            bottomSelector="footer"
            bottomOffset={3}
            rightOffset={3}
            topSelector="nav"
        >
          <i className="fas fa-angle-up" />
        </ScrollTopButton>
      </PageFrameGeneric>
    );
  }

  @action
  private handleCityBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    if (this.store.cityChanged && this.store.isCityValid) {
      this.store.cityChanged = false;

      const target = event.currentTarget;
      target.parentElement!.classList.add('is-loading');
      try {
        await request.patch('/user/profile/', { city: target.value });
        target.parentElement!.classList.remove('is-loading');
      } catch (err) {
        console.error(err);
      }
    }
  }

  @action
  private handleCityChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (this.store.city !== event.currentTarget.value) {
      this.store.city = event.currentTarget.value;
      this.store.cityChanged = true;
    }
  }

  @action
  private handleFirstNameBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    if (this.store.firstNameChanged && this.store.isFirstNameValid) {
      this.store.firstNameChanged = false;

      const target = event.currentTarget;
      target.parentElement!.classList.add('is-loading');
      try {
        await request.patch('/user/profile/', { first_name: target.value });
        target.parentElement!.classList.remove('is-loading');
      } catch (err) {
        console.error(err);
      }
    }
  }

  @action
  private handleFirstNameChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (this.store.firstName !== event.currentTarget.value) {
      this.store.firstName = event.currentTarget.value;
      this.store.firstNameChanged = true;
    }
  }

  @action
  private handleGenderChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Handle interests field separately
    switch (event.currentTarget.value) {
      case Gender.MALE:
        this.store.gender = Gender.MALE; break;
      case Gender.FEMALE:
        this.store.gender = Gender.FEMALE; break;
      default:
        this.store.gender = Gender.OTHER;
    }

    await request.patch('/user/profile/', { gender: this.store.gender.substr(0, 1) });
  }

  @action
  private handleInterestsBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    if (this.store.interestsChanged) {
      this.store.interestsChanged = false;

      const target = event.currentTarget;
      target.parentElement!.classList.add('is-loading');

      try {
        await request.patch('/user/profile/', { interests: target.value });
        target.parentElement!.classList.remove('is-loading');
      } catch (err) {
        console.error(err);
      }
    }
  }

  @action
  private handleInterestsChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (value && this.store.interests !== value) {
      this.store.interests = value;
      this.store.interestsChanged = true;
    }
  }

  @action
  private handleLastNameBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    if (this.store.lastNameChanged && this.store.isLastNameValid) {
      this.store.lastNameChanged = false;

      const target = event.currentTarget;
      target.parentElement!.classList.add('is-loading');
      try {
        await request.patch('/user/profile/', { last_name: target.value });
        target.parentElement!.classList.remove('is-loading');
      } catch (err) {
        console.error(err);
      }
    }
  }

  @action
  private handleLastNameChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (this.store.lastName !== event.currentTarget.value) {
      this.store.lastName = event.currentTarget.value;
      this.store.lastNameChanged = true;
    }
  }

  @action
  private handleStateBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    if (this.store.stateChanged && this.store.isStateValid) {
      this.store.stateChanged = false;

      const target = event.currentTarget;
      target.parentElement!.classList.add('is-loading');
      try {
        await request.patch('/user/profile/', { state_province: target.value });
        target.parentElement!.classList.remove('is-loading');
      } catch (err) {
        console.error(err);
      }
    }
  }

  @action
  private handleStateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (this.store.state !== event.currentTarget.value) {
      this.store.state = event.currentTarget.value;
      this.store.stateChanged = true;
    }
  }
}

export default Profile;


// WEBPACK FOOTER //
// ./src/pages/profile/Profile.tsx